<template>
  <b-modal
    :id="modalId"
    visible
    size="xl"
    modal-class="form-builder-modal"
    hide-footer
    hide-header
    :title="formName || 'Formular bearbeiten'"
    no-close-on-backdrop
    @hide="onModalHide"
    @hidden="$emit('closed')"
  >
    <iframe ref="frame" :src="builderUrl" class="form-builder-frame" @load="frameLoaded = true" />
    <modal-upload-image
      v-if="groupId"
      modal-id="form-builder-media"
      :group-id="groupId"
      :image-object="{ url: '', type: 'image', focalpoint: { x: 50, y: 50 } }"
      @image-uploaded="onMediaPicked"
      @cancel-modal="onMediaCancelled"
    />
  </b-modal>
</template>

<script>
import UpdateForm from '@/graphQlQueries/mutations/forms/updateForm';
import GetAllAvailableLanguages from '@/graphQlQueries/queries/getAllAvailableLanguages';
import GetAllTemplateForms from '@/graphQlQueries/queries/forms/getAllTemplateForms';
import languageHandleToLocale from '@/utils/languageHandleToLocale';
import { builderValueForms } from '@/components/settings/plugins/fieldMap';
import stripPathPrefix from '@/components/modals/forms/saveFormError';
import mapTemplateForms from '@/components/modals/forms/templateFormsFilter';

export default {
  name: 'FormBuilderModal',
  components: {
    ModalUploadImage: () => import('@/components/modals/ModalUploadImage.vue'),
  },
  props: {
    formId: { type: Number, required: true },
    formName: { type: String, default: '' },
    formData: { type: Object, default: null },
    groupId: { type: Number, default: null },
    // Forms of the forms admin; hidden fields may carry their variables.
    valueForms: { type: Array, default: () => [] },
    canUseValueSources: { type: Boolean, default: false },
  },
  data() {
    return {
      modalId: `form-builder-modal-${this.formId}`,
      isDirty: false,
      frameLoaded: false,
      builderReady: false,
      initSent: false,
      availableLanguages: [],
      languagesSettled: false,
      templateForms: [],
      templatesSettled: false,
      mediaRequestId: null,
    };
  },
  apollo: {
    availableLanguages: {
      query: GetAllAvailableLanguages,
      update(data) {
        return (data.languages || []).map((lang) => ({
          code: languageHandleToLocale(lang.handle),
          name: lang.name,
        }));
      },
      result() {
        this.languagesSettled = true;
        this.maybeSendInit();
      },
      error(err) {
        console.error('FormBuilderModal availableLanguages', err);
        this.availableLanguages = [];
        this.languagesSettled = true;
        this.maybeSendInit();
      },
    },
    templateForms: {
      query: GetAllTemplateForms,
      update: mapTemplateForms,
      result() {
        this.onTemplatesSettled();
      },
      error(err) {
        console.error('FormBuilderModal templateForms', err);
        this.templateForms = [];
        this.onTemplatesSettled();
      },
    },
  },
  computed: {
    builderUrl() {
      // editor-v2 reads the admin's UI language from ?uiLocale= (de-DE / en-US).
      const uiLocale = encodeURIComponent(this.$i18n.locale);
      return `${process.env.VUE_APP_EDITOR_V2_ENDPOINT}/?mode=form-builder&formId=${this.formId}&uiLocale=${uiLocale}`;
    },
    builderOrigin() {
      return new URL(this.builderUrl).origin;
    },
    templates() {
      return Array.isArray(this.templateForms) ? this.templateForms : [];
    },
    initialFormJson() {
      // Only hand the builder format-2 documents; anything older opens as a starter doc.
      let data = this.formData;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { data = null; }
      }
      return data && data.version === 2 ? data : null;
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    postToBuilder(type, payload) {
      const { frame } = this.$refs;
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ source: 'wbmg-form-builder', type, payload }, this.builderOrigin);
      }
    },
    onMessage(event) {
      if (event.origin !== this.builderOrigin) return;
      const data = event.data || {};
      if (data.source !== 'wbmg-form-builder') return;
      if (data.type === 'FORM_BUILDER_READY') {
        this.builderReady = true;
        this.maybeSendInit();
        // Don't block the builder forever on a hanging query — send what we have.
        setTimeout(() => this.maybeSendInit(true), 3000);
      } else if (data.type === 'FORM_CHANGED') {
        this.isDirty = !!(data.payload && data.payload.dirty);
      } else if (data.type === 'SAVE_FORM') {
        this.saveForm(data.payload);
      } else if (data.type === 'CLOSE_FORM_BUILDER') {
        this.tryClose(data.payload && data.payload.dirty);
      } else if (data.type === 'OPEN_MEDIA_PICKER') {
        this.openMediaPicker(data.payload);
      }
    },
    onTemplatesSettled() {
      this.templatesSettled = true;
      // Query settled after INIT already went out → follow-up message so the
      // builder's "Vorlagen" button still works for existing forms.
      if (this.initSent) this.postToBuilder('UPDATE_TEMPLATES', { templates: this.templates });
      else this.maybeSendInit();
    },
    maybeSendInit(force = false) {
      if (this.initSent || !this.builderReady) return;
      // Fresh forms wait for templates (auto template chooser); existing forms
      // don't block on the query — templates arrive later via UPDATE_TEMPLATES.
      if (!force && (!this.languagesSettled || (!this.initialFormJson && !this.templatesSettled))) return;
      this.initSent = true;
      this.postToBuilder('INIT_FORM_BUILDER', {
        formId: this.formId,
        formName: this.formName,
        formJson: this.initialFormJson,
        availableLanguages: this.availableLanguages,
        templates: this.templatesSettled ? this.templates : [],
        valueSourceForms: builderValueForms(this.valueForms),
        canUseValueSources: this.canUseValueSources,
      });
    },
    async saveForm(payload) {
      try {
        await this.$apollo.mutate({
          mutation: UpdateForm,
          variables: {
            obj: {
              form_id: payload.formId,
              form_data: payload.formJson,
              name: payload.formName,
            },
          },
        });
        this.isDirty = false;
        this.postToBuilder('FORM_SAVED', { ok: true });
        this.$emit('saved');
      } catch (err) {
        console.error('FormBuilderModal saveForm', err);
        // updateForm names the first failed rule and its path (webmag-database
        // updateForm.ts); the builder places it next to the field.
        const g = err && err.graphQLErrors && err.graphQLErrors[0];
        const { path, message } = stripPathPrefix(g);
        this.postToBuilder('FORM_SAVED', {
          ok: false,
          errors: [path
            ? { path, message }
            : { path: '/', message: 'Speichern fehlgeschlagen — bitte erneut versuchen.' }],
        });
      }
    },
    tryClose(dirty) {
      if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
      this.isDirty = false;
      this.$bvModal.hide(this.modalId);
    },
    onModalHide(event) {
      if (this.isDirty && event.trigger && event.trigger !== 'event') {
        if (!window.confirm('Ungespeicherte Änderungen verwerfen?')) event.preventDefault();
      }
    },
    openMediaPicker(payload) {
      if (!payload || !payload.requestId) return;
      if (!this.groupId) {
        // No group context (should not happen from the forms area) — answer
        // immediately so the builder's promise settles instead of hanging.
        this.postToBuilder('MEDIA_PICKED', { requestId: payload.requestId });
        return;
      }
      this.mediaRequestId = payload.requestId;
      this.$root.$emit('bv::show::modal', 'form-builder-media');
    },
    onMediaPicked(imageObj) {
      if (!this.mediaRequestId) return;
      this.postToBuilder('MEDIA_PICKED', {
        requestId: this.mediaRequestId,
        url: (imageObj && (imageObj.url || imageObj.src)) || undefined,
        alt: (imageObj && imageObj.alt) || '',
      });
      this.mediaRequestId = null;
    },
    onMediaCancelled() {
      if (!this.mediaRequestId) return;
      this.postToBuilder('MEDIA_PICKED', { requestId: this.mediaRequestId });
      this.mediaRequestId = null;
    },
  },
};
</script>

<!-- unscoped on purpose: b-modal renders outside this component's root -->
<style>
.form-builder-modal .modal-dialog { max-width: 96vw; height: 94vh; margin: 3vh auto; }
.form-builder-modal .modal-content { height: 100%; }
.form-builder-modal .modal-body { padding: 0; height: 100%; }
.form-builder-frame { width: 100%; height: 100%; border: 0; display: block; }
</style>
